import { pages, subPackages, tabBar } from '@/pages.json'
import { isMp } from './platform'
import dayjs, { extend } from 'dayjs'
import timezone from 'dayjs/plugin/timezone'
import utc from 'dayjs/plugin/utc'
import qs from 'qs'

extend(utc)
extend(timezone)

export const getLastPage = () => {
  // getCurrentPages() 至少有1个元素，所以不再额外判断
  // const lastPage = getCurrentPages().at(-1)
  // 上面那个在低版本安卓中打包回报错，所以改用下面这个【虽然我加了src/interceptions/prototype.ts，但依然报错】
  const pages = getCurrentPages()
  return pages[pages.length - 1]
}

/** 判断当前页面是否是tabbar页  */
export const getIsTabbar = () => {
  if (!tabBar) {
    return false
  }
  if (!tabBar.list.length) {
    // 通常有tabBar的话，list不能有空，且至少有2个元素，这里其实不用处理
    return false
  }
  const lastPage = getLastPage()
  const currPath = lastPage.route
  return !!tabBar.list.find((e) => e.pagePath === currPath)
}

/**
 * 获取当前页面路由的 path 路径和 redirectPath 路径
 * path 如 ‘/pages/login/index’
 * redirectPath 如 ‘/pages/demo/base/route-interceptor’
 */
export const currRoute = () => {
  const lastPage = getLastPage()
  const currRoute = (lastPage as any).$page
  // console.log('lastPage.$page:', currRoute)
  // console.log('lastPage.$page.fullpath:', currRoute.fullPath)
  // console.log('lastPage.$page.options:', currRoute.options)
  // console.log('lastPage.options:', (lastPage as any).options)
  // 经过多端测试，只有 fullPath 靠谱，其他都不靠谱
  const { fullPath } = currRoute as { fullPath: string }
  // console.log(fullPath)
  // eg: /pages/login/index?redirect=%2Fpages%2Fdemo%2Fbase%2Froute-interceptor (小程序)
  // eg: /pages/login/index?redirect=%2Fpages%2Froute-interceptor%2Findex%3Fname%3Dfeige%26age%3D30(h5)
  return getUrlObj(fullPath)
}

const ensureDecodeURIComponent = (url: string) => {
  if (url.startsWith('%')) {
    return ensureDecodeURIComponent(decodeURIComponent(url))
  }
  return url
}
/**
 * 解析 url 得到 path 和 query
 * 比如输入url: /pages/login/index?redirect=%2Fpages%2Fdemo%2Fbase%2Froute-interceptor
 * 输出: {path: /pages/login/index, query: {redirect: /pages/demo/base/route-interceptor}}
 */
export const getUrlObj = (url: string) => {
  const [path, queryStr] = url.split('?')
  // console.log(path, queryStr)

  if (!queryStr) {
    return {
      path,
      query: {},
    }
  }
  const query: Record<string, string> = {}
  queryStr.split('&').forEach((item) => {
    const [key, value] = item.split('=')
    // console.log(key, value)
    query[key] = ensureDecodeURIComponent(value) // 这里需要统一 decodeURIComponent 一下，可以兼容h5和微信y
  })
  return { path, query }
}
/**
 * 得到所有的需要登录的pages，包括主包和分包的
 * 这里设计得通用一点，可以传递key作为判断依据，默认是 notNeedLogin, 与 route-block 配对使用
 * 如果没有传 key，则表示所有的pages，如果传递了 key, 则表示通过 key 过滤
 */
export const getAllPages = (key = 'notNeedLogin') => {
  // 这里处理主包
  const mainPages = [
    ...pages
      .filter((page) => {
        if (!key) return false
        return page[key] === true
      })
      .map((page) => ({
        ...page,
        path: `/${page.path}`,
      })),
  ]
  // 这里处理分包
  const subPages: any[] = []
  subPackages.forEach((subPageObj) => {
    // console.log(subPageObj)
    const { root } = subPageObj

    subPageObj.pages
      .filter((page) => !key || page[key])
      .forEach((page: { path: string } & Record<string, any>) => {
        subPages.push({
          ...page,
          path: `/${root}/${page.path}`,
        })
      })
  })
  const result = [...mainPages, ...subPages]
  // console.log(`getAllPages by ${key} result: `, result)
  return result
}

/**
 * 得到所有的需要登录的pages，包括主包和分包的
 * 只得到 path 数组
 */
export const getNotNeedLoginPages = (): string[] =>
  getAllPages('notNeedLogin').map((page) => page.path)

/**
 * 得到所有的需要登录的pages，包括主包和分包的
 * 只得到 path 数组
 */
export const notNeedLoginPages: string[] = getAllPages('notNeedLogin').map((page) => page.path)

/**
 * 根据微信小程序当前环境，判断应该获取的BaseUrl
 */
export const getEnvBaseUrl = () => {
  // 请求基准地址
  const baseUrl = import.meta.env.VITE_SERVER_BASEURL

  return baseUrl
}

/**
 * 根据微信小程序当前环境，判断应该获取的UPLOAD_BASEURL
 */
export const getEnvBaseUploadUrl = () => {
  // 请求基准地址
  let baseUploadUrl = import.meta.env.VITE_UPLOAD_BASEURL
  if (JSON.parse(__VITE_APP_PROXY__)) {
    baseUploadUrl = `${import.meta.env.VITE_APP_PROXY_PREFIX}/${baseUploadUrl.split(import.meta.env.VITE_APP_PROXY_PREFIX + '/')[1]}`
  }

  // 小程序端环境区分
  if (isMp) {
    const {
      miniProgram: { envVersion },
    } = uni.getAccountInfoSync()

    switch (envVersion) {
      case 'develop':
        baseUploadUrl = 'https://ukw0y1.laf.run/upload'
        break
      case 'trial':
        baseUploadUrl = 'https://ukw0y1.laf.run/upload'
        break
      case 'release':
        baseUploadUrl = 'https://ukw0y1.laf.run/upload'
        break
    }
  }

  return baseUploadUrl
}

export function mergeStep(wrapped: (...rest: any[]) => Promise<any>) {
  let _mergeStepRequestInstance: any = null
  return async function (...args) {
    if (this?._mergeStepRequestInstance ?? _mergeStepRequestInstance)
      return this?._mergeStepRequestInstance ?? _mergeStepRequestInstance
    if (this) {
      this._mergeStepRequestInstance = wrapped.apply(this, args)
      if (Object.prototype.toString.call(this?._mergeStepRequestInstance) === '[object Promise]') {
        this?._mergeStepRequestInstance.finally(() => {
          this._mergeStepRequestInstance = null
        })
      }
      return this._mergeStepRequestInstance
    } else {
      _mergeStepRequestInstance = wrapped(...args)
      if (Object.prototype.toString.call(_mergeStepRequestInstance) === '[object Promise]') {
        _mergeStepRequestInstance.finally(() => {
          _mergeStepRequestInstance = null
        })
      }
      return _mergeStepRequestInstance
    }
  }
}

// Promise 版modal
export const showModal = (
  content: string,
  {
    showCancel,
    class: className,
    title,
    ...options
  }: UniApp.ShowModalOptions & {
    class?: 'danger-modal' | 'primary-modal' | 'default' | 'success-modal'
  } = {},
) => {
  return new Promise((resolve, reject) =>
    uni.showModal({
      showCancel: showCancel === true,
      title: title || '提示',
      ...options,
      class: className ?? 'default',
      content,
      success(e) {
        if (e.confirm) {
          return resolve(e)
        }
        reject(e)
      },
      fail: reject,
    }),
  )
}

/** 格式化时间 */
export const formatTime = (time: string | number, format = 'YYYY-MM-DD HH:mm:ss') => {
  // 判断time是否是utc时间
  const isUtc = time.toString().includes('Z')
  if (isUtc) {
    return dayjs(time).tz('Asia/Shanghai').format(format)
  }
  return dayjs(time).format(format)
}

/** 获取图片url */
export const getImageUrl = (image: any) => {
  if (typeof image === 'string') {
    return /^http|data:/.test(image) ? image : `${import.meta.env.VITE_SERVER_BASEURL}${image}`
  }
  return image
}

/** 格式化金额 */
export const formatMoney = (money: string | number) => {
  return String(money).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}
export const openPayUrl = async (url: string) => {
  // 判断苹果设备需要弹出提示
  if (uni.getSystemInfoSync().platform === 'ios') {
    await showModal('跳转支付页面?', { showCancel: true })
  }
  window.open(url, '_blank')
}

// 打开支付页面，并定时刷新支付信息
let timer = null
export const openPayPage = async ({
  payUrl,
  callback,
}: {
  payUrl: string
  callback: () => void
}) => {
  // 判断苹果设备需要弹出提示
  if (uni.getSystemInfoSync().platform === 'ios') {
    await showModal('跳转支付页面?', {
      showCancel: true,
    })
  }
  window.open(payUrl, '_blank')
  clearInterval(timer)
  timer = setInterval(callback, 5000)
}
export const navigateBack = () => {
  const showBack = getCurrentPages().length > 1
  if (showBack) {
    uni.navigateBack()
  } else {
    uni.reLaunch({
      url: '/',
    })
  }
}

export const navigateTo = (url: string = '', type: 'navigateTo' | 'redirectTo' = 'navigateTo') => {
  if (!url) {
    return showModal('链接不存在')
  }

  const isHttp = url.startsWith('http')
  if (isHttp) {
    return (location.href = addBrowserOpen(url))
  }

  const lastPage = getLastPage()

  if (checkTargetUrlIsTabbar(url)) {
    uni.switchTab({ url })
  } else if (url.includes(lastPage.route)) {
    uni.redirectTo({ url })
  } else {
    if (type === 'navigateTo') {
      uni.navigateTo({ url })
    } else if (type === 'redirectTo') {
      uni.redirectTo({ url })
    } else if (type === 'switchTab') {
      uni.switchTab({ url })
    } else {
      uni.navigateTo({ url })
    }
  }
}
/** 判断目标页面是否为tabbar页 */
export const checkTargetUrlIsTabbar = (url: string = '') => {
  if (!tabBar) {
    return false
  }
  if (!tabBar.list.length) {
    // 通常有tabBar的话，list不能有空，且至少有2个元素，这里其实不用处理
    return false
  }
  return !!tabBar.list.find((e) => url?.includes(e.pagePath))
}

export const showToast = (
  title: string,
  icon: 'none' | 'success' | 'loading' | 'error' | 'fail' | 'exception' = 'none',
) => {
  uni.showToast({
    title,
    icon,
  })
}

export function timingSequenceStep(wrapped: (...rest: any[]) => Promise<any>, delay = 0) {
  let _runningId: symbol
  let _timer
  return async function (...args) {
    const instanceId = Symbol('instanceId')
    if (this) {
      this._runningId = instanceId
    } else {
      _runningId = instanceId
    }
    clearTimeout(_timer)
    return new Promise<any>((resolve, reject) => {
      _timer = setTimeout(() => {
        const fun = wrapped.apply(this, args)
        if (Object.prototype.toString.call(fun) === '[object Promise]') {
          return fun
            .then((res: unknown) => {
              if (instanceId === (this?._runningId ?? _runningId)) {
                resolve(res)
              } else {
                // eslint-disable-next-line prefer-promise-reject-errors
                reject({
                  instanceId,
                  function: wrapped,
                  msg: 'Promise被取消，因为有新的Promise被执行',
                })
              }
            })
            .catch(reject)
        }
        resolve(fun)
      }, delay)
    })
  }
}

// 浏览图片
export const previewImages = (images: string[], index = 0) => {
  uni.previewImage({
    urls: images,
    current: images[index],
  })
}
// 为url额外添加参数，如果是hash路由，则添加到hash后面，否则添加到query后面
// force 为true时，会覆盖原有的参数
// 比如：
// 输入：https://www.baidu.com/s?wd=hello&a=1
// 输出：https://www.baidu.com/s?wd=hello&a=1&b=2
// 输入：https://www.baidu.com/s#hello?a=1
// 输出：https://www.baidu.com/s#hello?a=1&b=2
export const addUrlParam = (url: string, param: Record<string, string | number>, force = false) => {
  const urlObj = new URL(url)

  // 检查是否有hash部分
  if (urlObj.hash) {
    // 处理hash路由，参数添加到hash后面
    const hashParts = urlObj.hash.split('?')
    const hashPath = hashParts[0]
    const hashParams = new URLSearchParams(hashParts[1] || '')

    Object.entries(param).forEach(([key, value]) => {
      if (force || !hashParams.has(key)) {
        hashParams.set(key, value)
      }
    })

    urlObj.hash = hashPath + (hashParams.toString() ? `?${hashParams.toString()}` : '')
  } else {
    // 处理普通路由，参数添加到query后面
    const searchParams = new URLSearchParams(urlObj.search)
    Object.entries(param).forEach(([key, value]) => {
      if (force || !searchParams.has(key)) {
        searchParams.set(key, value)
      }
    })
    urlObj.search = searchParams.toString()
  }

  return urlObj.toString()
}

// 为react native添加browser_open标识
export const addBrowserOpen = (url: string) => {
  const isFromRN = (window as any).ReactNativeWebView === true
  if (isFromRN) {
    return addUrlParam(url, { browser_open: 1 })
  } else {
    return url
  }
}
